import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { EntryFeedback } from '../store/conversationSlice';

interface FeedbackPopoverProps {
  currentFeedback?: EntryFeedback;
  onSubmit: (rating: 'up' | 'down', reason: string) => Promise<void>;
  disabled?: boolean;
}

const FeedbackPopover: React.FC<FeedbackPopoverProps> = ({ currentFeedback, onSubmit, disabled = false }) => {
  const [pendingRating, setPendingRating] = useState<'up' | 'down' | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [popoverPos, setPopoverPos] = useState<{ top: number; right: number } | null>(null);

  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Recalculate position whenever the popover opens
  const calcPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPopoverPos({
      top: rect.bottom + 6,
      right: window.innerWidth - rect.right,
    });
  }, []);

  // Close when clicking outside
  useEffect(() => {
    if (pendingRating === null) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) {
        closePopover();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [pendingRating]);

  // Reposition if window resizes while open
  useEffect(() => {
    if (pendingRating === null) return;
    const onResize = () => calcPosition();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [pendingRating, calcPosition]);

  const openPopover = (rating: 'up' | 'down') => {
    if (disabled) return;
    calcPosition();
    setPendingRating(rating);
    // Pre-populate reason from existing feedback so the user can edit it
    // (covers both re-rating the same direction and switching up <-> down)
    setReason(currentFeedback?.reason || '');
    setSubmitError(null);
  };

  const closePopover = () => {
    setPendingRating(null);
    setReason('');
    setSubmitError(null);
  };

  const handleSubmit = async () => {
    if (!pendingRating || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onSubmit(pendingRating, reason.trim());
      closePopover();
    } catch {
      setSubmitError('Failed to save. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const isUp = currentFeedback?.rating === 'up';
  const isDown = currentFeedback?.rating === 'down';

  return (
    <>
      <div ref={triggerRef} className="inline-flex items-center gap-0.5">
        <button
          onClick={() => openPopover('up')}
          disabled={disabled}
          title="Helpful"
          className={`p-1 rounded transition-colors ${
            isUp ? 'text-green-600' : 'text-gray-400 hover:text-green-600'
          } disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          <ThumbsUp className="w-4 h-4" />
        </button>
        <button
          onClick={() => openPopover('down')}
          disabled={disabled}
          title="Not helpful"
          className={`p-1 rounded transition-colors ${
            isDown ? 'text-red-500' : 'text-gray-400 hover:text-red-500'
          } disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          <ThumbsDown className="w-4 h-4" />
        </button>
      </div>

      {pendingRating !== null && popoverPos && (
        <div
          ref={popoverRef}
          style={{
            position: 'fixed',
            top: popoverPos.top,
            right: popoverPos.right,
            zIndex: 99999,
            width: 256,
          }}
          className="bg-white border border-gray-200 rounded-lg shadow-xl p-3"
        >
          <p className="text-xs font-semibold text-gray-700 mb-2">
            {pendingRating === 'up' ? 'Marking as helpful' : 'Marking as not helpful'}
          </p>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Why? (optional)"
            rows={2}
            autoFocus
            className="w-full text-xs border border-gray-200 rounded px-2 py-1.5 resize-none focus:outline-none focus:border-blue-400 mb-2"
          />
          {submitError && (
            <p className="text-xs text-red-600 mb-2">{submitError}</p>
          )}
          <div className="flex gap-2 justify-end">
            <button
              onClick={closePopover}
              className="text-xs px-2 py-1 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? 'Saving...' : 'Submit'}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackPopover;
